import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type {
    ApiEmptyResponse,
    ApiOrderResponse,
    EvmFullFillCreateOrder,
    EvmFullFillOrderList,
    EvmListing,
    EvmOrderData,
    OrderListParams
} from '~/service/api/types';
import fallbackApi from '../fallbackApi';

export default (axiosInstance: AxiosInstance) => ({
    /**
   * @description get list of nft offers
   *
   * @tags offer
   * @name GetOfferList
   * @request GET:/offers/list
   * @response `200` `GetOfferListData` success getting order list
   * @response `default` `EmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any data answers
   */
    async listOrder(params?: OrderListParams) {
        const fallbackConfig = fallbackApi<ApiOrderResponse<EvmListing[]>>({
            text: 'Loading offer list',
            description: 'An error occurred while loading offer list',
            visible: 'error',
            exclude: [
                404,
                403
            ],
            fallback: {
                data: [],
                pagination: {
                    page: 0,
                    size: 0,
                    total: 0
                },
                status: {
                    code: 404,
                    message: 'Response fallbackApi get offer list',
                    msgId: '1'
                }
            }
        });
        const config: AxiosRequestConfig = {
            params: {
                ...params
                    ? { ...params }
                    : {}
            },
            ...fallbackConfig
        };

        const res = await axiosInstance.get<ApiOrderResponse<EvmListing[]>>(
            `/v1/orders/list`,
            config
        );
        return res.data;
    },

    /**
   * @description fill order
   *
   * @tags order
   * @name fillOrder
   * @request POST:/order
   * @response `default` `ApiEmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
   */
    async fillOrder(query: {
        user: string;
        chain: string;
    },
    request: Omit<EvmOrderData, 'parameters'> & {
        parameters: {
            startTime: string;
            endTime: string;
        } & Omit<EvmOrderData['parameters'], 'startTime' | 'endTime'>;
    }) {
        const res = await axiosInstance.post<ApiEmptyResponse>(
            '/v1/order',
            request,
            {
                params: {
                    ...query
                }
            }
        );
        return res.data;
    },

    /**
   * @description get nft listing with smallest price
   *
   * @tags order
   * @name bestOrders
   * @request POST:/v2/orders/best
   * @response `200` `ApiOrderResponse<EvmFullFillOrderList>` success getting best order
   * @response `default` `EmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
   */
    async bestOrders(uuids: string[]) {
        try {
            const res = await axiosInstance.post<ApiOrderResponse<EvmFullFillOrderList>>(
                '/v2/orders/best',
                {
                    uuids
                },
                { timeout: 10000 }
            );
            return res.data;
        }
        catch (err) {
            const error = err as Error;
            throw new Error(error.message);
        }
    },

    /**
     * @description get nft with smallest price
     *
     * @tags order
     * @name bestOrder
     * @request POST:/order/best
     * @response `200` `ApiOrderResponse<EvmFullFillCreateOrder>` success getting best order
     * @response `default` `EmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
     */
    async bestOrder(uuid: string) {
        try {
            const res = await axiosInstance.post<ApiOrderResponse<EvmFullFillCreateOrder>>(
                '/v2/order/best',
                {
                    uuid
                },
                { timeout: 10000 }
            );
            return res.data;
        }
        catch (err) {
            const error = err as Error;
            throw new Error(error.message);
        }
    },

    /**
   * @description cancel created order listing
   *
   * @tags order
   * @name cancelOrder
   * @request POST:/order/cancel
   * @secure
   * @response `default` `ApiEmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
   */
    async cancelOrder(query: {
        hash: string;
    }) {
        const res = await axiosInstance.post<ApiEmptyResponse>(
            '/v1/orders/cancel',
            {},
            {
                params: {
                    ...query
                },
                timeout: 10000
            }
        );
        return res.data;
    },

    /**
   * @description update order
   *
   * @tags order
   * @name updateOrder
   * @request POST:/order/update
   * @secure
   * @response `default` `ApiEmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
   */
    async updateOrder(query: {
        hash: string;
    },
    request: EvmOrderData) {
        const res = await axiosInstance.post<ApiEmptyResponse>(
            '/v1/orders/update',
            request,
            {
                params: {
                    ...query
                },
                timeout: 10000
            }
        );
        return res.data;
    }
} as const);
